import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { functions as configuredFunctions } from '../config/firebase';

function normalizeEmail(email) {
  return String(email || '').trim().toLowerCase();
}

function displayNameFromUser(userDoc) {
  if (!userDoc) return 'Someone';
  const first = String(userDoc.firstName || '').trim();
  const last = String(userDoc.lastName || '').trim();
  const full = `${first} ${last}`.trim();
  return full || String(userDoc.username || userDoc.email || 'Someone').trim();
}

async function findUserByEmail(db, email) {
  const emailLower = normalizeEmail(email);
  if (!emailLower) return null;
  const snap = await getDocs(query(collection(db, 'users'), where('email', '==', emailLower)));
  if (snap.empty) return null;
  const d = snap.docs[0];
  return { id: d.id, ...d.data() };
}

async function findOrCreateDirectConversation(db, userA, userB, meta = {}) {
  const participants = [userA, userB].sort();
  const snap = await getDocs(
    query(collection(db, 'conversations'), where('participants', 'array-contains', userA), where('type', '==', 'direct'))
  );
  const existing = snap.docs.find((d) => {
    const p = d.data()?.participants || [];
    return p.length === 2 && p.includes(userB);
  });
  if (existing) return existing.id;

  const ref = await addDoc(collection(db, 'conversations'), {
    participants,
    type: 'direct',
    name: null,
    lastMessage: null,
    lastActivity: serverTimestamp(),
    createdAt: serverTimestamp(),
    createdBy: userA,
    unreadCounts: { [userA]: 0, [userB]: 0 },
    isActive: true,
    ...meta,
  });
  return ref.id;
}

async function sendTourInviteEmail(recipient, emailData, { throwOnError = false } = {}) {
  try {
    const handleEmail = httpsCallable(configuredFunctions || getFunctions(), 'handleEmail');
    await handleEmail({
      template: 'tour_invite',
      recipient,
      emailData,
    });
  } catch (err) {
    console.warn('Tour invite email failed', err);
    if (throwOnError) {
      const code = String(err?.code || '');
      if (code.includes('unavailable') || code.includes('internal')) {
        throw new Error(
          'Could not send the invitation email. Check your connection, or if developing locally start the Functions emulator (REACT_APP_USE_FUNCTIONS_EMULATOR=true).'
        );
      }
      throw new Error(err?.message || 'Could not send invite email.');
    }
  }
}

async function notifyTourInviteInApp(db, {
  ownerId,
  ownerName,
  inviteeUserId,
  inviteId,
  tourId,
  tourName,
  inviteMessage,
  reminder = false,
}) {
  const conversationId = await findOrCreateDirectConversation(db, ownerId, inviteeUserId);
  const preview = reminder
    ? `Tour invite reminder: ${tourName || 'Tour'}`
    : `Tour invite: ${tourName || 'Tour'}`;
  const text = reminder ? `Reminder: ${inviteMessage}` : inviteMessage;

  await addDoc(collection(db, 'conversations', conversationId, 'messages'), {
    text,
    senderId: ownerId,
    senderName: ownerName || 'Tour owner',
    senderPhoto: '',
    timestamp: serverTimestamp(),
    type: 'tour_invite',
    tourInviteId: inviteId,
    tourId,
    tourName: String(tourName || 'Tour').trim(),
    inviteStatus: 'pending',
    readBy: { [ownerId]: serverTimestamp() },
    edited: false,
  });
  const convSnap = await getDoc(doc(db, 'conversations', conversationId));
  const prevUnread = convSnap.data()?.unreadCounts?.[inviteeUserId] || 0;
  await updateDoc(doc(db, 'conversations', conversationId), {
    lastMessage: {
      text: preview,
      senderId: ownerId,
      timestamp: serverTimestamp(),
      type: 'tour_invite',
    },
    lastActivity: serverTimestamp(),
    [`unreadCounts.${inviteeUserId}`]: prevUnread + 1,
  });
}

/**
 * Create a tour invite, notify invitee in-app (and by email when possible).
 */
export async function createTourInvite({
  ownerId,
  ownerName,
  ownerEmail,
  tourId,
  tourName,
  inviteeEmail,
  inviteeName = '',
  inviteePhone = '',
  message = '',
  days = null,
}) {
  const db = getFirestore();
  const emailLower = normalizeEmail(inviteeEmail);
  if (!ownerId || !tourId || !emailLower) {
    throw new Error('Owner, tour, and invitee email are required.');
  }

  const existingSnap = await getDocs(
    query(
      collection(db, 'tourInvites'),
      where('ownerId', '==', ownerId),
      where('tourId', '==', tourId),
      where('inviteeEmail', '==', emailLower)
    )
  );
  const activeExisting = existingSnap.docs.find((d) => {
    const s = String(d.data()?.status || 'pending').toLowerCase();
    return !['declined', 'revoked', 'cancelled'].includes(s);
  });
  if (activeExisting) {
    throw new Error('This person already has an active invite for this tour.');
  }

  const inviteeUser = await findUserByEmail(db, emailLower);
  const inviteRef = await addDoc(collection(db, 'tourInvites'), {
    ownerId,
    ownerName: String(ownerName || 'Tour owner').trim(),
    ownerEmail: normalizeEmail(ownerEmail),
    tourId,
    tourName: String(tourName || 'Tour').trim(),
    inviteeEmail: emailLower,
    inviteeName: String(inviteeName || '').trim(),
    inviteePhone: String(inviteePhone || '').trim(),
    inviteeId: inviteeUser?.id || '',
    inviteePhotoURL: String(inviteeUser?.photoURL || '').trim(),
    inviteeUserType: String(inviteeUser?.userType || '').trim(),
    inviteeInterests: Array.isArray(inviteeUser?.interests) ? inviteeUser.interests : [],
    status: 'pending',
    message: String(message || '').trim(),
    days: Array.isArray(days) ? days : null,
    invitedAt: serverTimestamp(),
    createdAt: serverTimestamp(),
  });

  const inviteId = inviteRef.id;
  const inviteMessage = message.trim()
    || `You've been invited to join the tour "${tourName || 'Tour'}" on Venue Report Card.`;

  if (inviteeUser?.id) {
    await notifyTourInviteInApp(db, {
      ownerId,
      ownerName,
      inviteeUserId: inviteeUser.id,
      inviteId,
      tourId,
      tourName,
      inviteMessage,
    });
  }

  await sendTourInviteEmail(emailLower, {
    inviterName: ownerName || 'Someone',
    tourName: tourName || 'Tour',
    message: inviteMessage,
    appUrl: typeof window !== 'undefined' ? window.location.origin : 'https://vrc.app',
  });

  return inviteId;
}

/** Real-time invites sent for a tour (owner view). */
export function subscribeTourInvitesForTour(db, ownerId, tourId, callback) {
  if (!ownerId || !tourId) return () => {};
  const q = query(
    collection(db, 'tourInvites'),
    where('ownerId', '==', ownerId),
    where('tourId', '==', tourId)
  );
  return onSnapshot(q, (snap) => {
    const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    rows.sort((a, b) => {
      const at = a.invitedAt?.toMillis?.() || 0;
      const bt = b.invitedAt?.toMillis?.() || 0;
      return bt - at;
    });
    callback(rows);
  }, (err) => {
    console.warn('subscribeTourInvitesForTour', err);
    callback([]);
  });
}

/** Pending tour invites for the signed-in user. */
export function subscribePendingTourInvitesForUser(db, uid, email, callback) {
  if (!uid && !email) return () => {};
  const emailLower = normalizeEmail(email);
  const results = new Map();

  const emit = () => {
    const rows = [...results.values()].sort((a, b) => {
      const at = a.invitedAt?.toMillis?.() || 0;
      const bt = b.invitedAt?.toMillis?.() || 0;
      return bt - at;
    });
    callback(rows.filter((r) => String(r.status || 'pending').toLowerCase() === 'pending'));
  };

  const unsubs = [];
  if (uid) {
    unsubs.push(onSnapshot(query(collection(db, 'tourInvites'), where('inviteeId', '==', uid)), (snap) => {
      snap.docs.forEach((d) => results.set(d.id, { id: d.id, ...d.data() }));
      emit();
    }));
  }
  if (emailLower) {
    unsubs.push(onSnapshot(query(collection(db, 'tourInvites'), where('inviteeEmail', '==', emailLower)), (snap) => {
      snap.docs.forEach((d) => results.set(d.id, { id: d.id, ...d.data() }));
      emit();
    }));
  }
  return () => unsubs.forEach((u) => u());
}

export async function respondToTourInvite(inviteId, status, responderUid) {
  const db = getFirestore();
  const normalized = String(status || '').toLowerCase();
  if (!['accepted', 'declined'].includes(normalized)) {
    throw new Error('Invalid response');
  }
  const ref = doc(db, 'tourInvites', inviteId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('Invite not found');
  const data = snap.data() || {};
  if (responderUid) {
    const user = await findUserByEmail(db, data.inviteeEmail);
    if (!user || user.id !== responderUid) throw new Error('Not authorized');
  }
  await updateDoc(ref, {
    status: normalized,
    respondedAt: serverTimestamp(),
    inviteeId: data.inviteeId || responderUid || '',
  });

  if (responderUid && data.ownerId) {
    try {
      const conversationId = await findOrCreateDirectConversation(db, data.ownerId, responderUid);
      await addDoc(collection(db, 'conversations', conversationId, 'messages'), {
        text: normalized === 'accepted'
          ? `Accepted the tour invite for "${data.tourName || 'Tour'}".`
          : `Declined the tour invite for "${data.tourName || 'Tour'}".`,
        senderId: responderUid,
        senderName: displayNameFromUser(await getDoc(doc(db, 'users', responderUid)).then((s) => s.data())),
        timestamp: serverTimestamp(),
        type: 'system',
        readBy: { [responderUid]: serverTimestamp() },
      });
    } catch (err) {
      console.warn('respondToTourInvite notify', err);
    }
  }

  if (normalized === 'accepted' && data.ownerId && data.tourId) {
    try {
      const sync = httpsCallable(configuredFunctions || getFunctions(), 'syncTourPartyCrewCoverage');
      await sync({ tourId: data.tourId, ownerId: data.ownerId });
    } catch (err) {
      console.warn('respondToTourInvite crew coverage sync', err);
    }
  }
}

export async function revokeTourInvite(inviteId, ownerId) {
  const db = getFirestore();
  const ref = doc(db, 'tourInvites', inviteId);
  const snap = await getDoc(ref);
  if (!snap.exists() || snap.data()?.ownerId !== ownerId) throw new Error('Not authorized');
  await updateDoc(ref, { status: 'revoked', updatedAt: serverTimestamp() });
}

/** Re-send a pending tour invite email (and in-app notification when the invitee has an account). */
export async function resendTourInvite(inviteId, ownerId) {
  const db = getFirestore();
  const ref = doc(db, 'tourInvites', inviteId);
  const snap = await getDoc(ref);
  if (!snap.exists() || snap.data()?.ownerId !== ownerId) throw new Error('Not authorized');

  const data = snap.data() || {};
  const status = String(data.status || 'pending').toLowerCase();
  if (status !== 'pending') throw new Error('Only pending invites can be resent.');

  const emailLower = normalizeEmail(data.inviteeEmail);
  if (!emailLower) throw new Error('Invite is missing an email address.');

  const tourName = String(data.tourName || 'Tour').trim();
  const inviteMessage = String(data.message || '').trim()
    || `You've been invited to join the tour "${tourName}" on Venue Report Card.`;

  let inviteeUser = null;
  if (data.inviteeId) {
    const userSnap = await getDoc(doc(db, 'users', data.inviteeId));
    if (userSnap.exists()) inviteeUser = { id: userSnap.id, ...userSnap.data() };
  }
  if (!inviteeUser) {
    inviteeUser = await findUserByEmail(db, emailLower);
  }

  if (inviteeUser?.id) {
    await notifyTourInviteInApp(db, {
      ownerId: data.ownerId,
      ownerName: data.ownerName,
      inviteeUserId: inviteeUser.id,
      inviteId,
      tourId: data.tourId,
      tourName,
      inviteMessage,
      reminder: true,
    });
    if (!data.inviteeId) {
      await updateDoc(ref, { inviteeId: inviteeUser.id });
    }
  }

  await sendTourInviteEmail(emailLower, {
    inviterName: data.ownerName || 'Someone',
    tourName,
    message: inviteMessage,
    appUrl: typeof window !== 'undefined' ? window.location.origin : 'https://vrc.app',
  }, { throwOnError: true });

  await updateDoc(ref, {
    lastResentAt: serverTimestamp(),
    resentCount: (data.resentCount || 0) + 1,
  });
}
